import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { IconSortyBy } from "../../../assets/icons/icon-sortby-arrow";

const SortBy = ({ handleSort }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [sortOrder, setSortOrder] = useState("firstName");
  const dropdownRef = useRef();

  const navigate = useNavigate();
  const location = useLocation();

  const options = [
    { label: "Newest", value: "firstName" },
    { label: "Oldest", value: "-firstName" },
  ];

  // ✅ Read or set "sorting" from query params on load
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const querySort = params.get("sorting");

    if (querySort) {
      // If sorting param exists and valid, use it
      const validOption = options.find((opt) => opt.value === querySort);
      if (validOption) {
        setSortOrder(querySort);
        handleSort(querySort, false, true);
      } else {
        // Invalid param, reset to default
        params.set("sorting", "firstName");
        navigate(`${location.pathname}?${params.toString()}`, { replace: true });
        setSortOrder("firstName");
        handleSort("firstName", false, true);
      }
    } else {
      // No param, push default firstName
      params.set("sorting", "firstName");
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
      setSortOrder("firstName");
      handleSort("firstName", false, true);
    }
  }, [location.search]);

  // ✅ Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ Close dropdown when scrolling
  useEffect(() => {
    const handleScroll = () => {
      if (isOpen) setIsOpen(false);
    };
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [isOpen]);

  const handleSelect = (value) => {
    setSortOrder(value);
    setIsOpen(false);
    handleSort(value, false, true);

    // ✅ Update query param
    const params = new URLSearchParams(location.search);
    params.set("sorting", value);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  const selectedLabel =
    options.find((opt) => opt.value === sortOrder)?.label || "Sort by";

  return (
    <div
      className="relative inline-block mt-[10px] title_color"
      ref={dropdownRef}
    >
      <div className="flex items-center">
        <span>Sort by:</span>
        <div
          onClick={() => setIsOpen(!isOpen)}
          className="cursor-pointer flex items-center gap-1 font-[500] pl-[10px] pr-[20px]"
        >
          <span>{selectedLabel}</span>
          <span
            className={`transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
          >
            <IconSortyBy />
          </span>
        </div>
      </div>

      {isOpen && (
        <div className="absolute right-6 z-10 mt-1 bg-white border border-gray-300 shadow-md rounded-md inline-block whitespace-nowrap">
          {options.map((opt, idx) => (
            <div
              key={opt.value}
              onClick={() => handleSelect(opt.value)}
              className={`cursor-pointer px-3 py-2 text-sm ${
                sortOrder === opt.value ? "bg-[#245cea] text-white" : ""
              } ${
                idx === 0
                  ? "hover:bg-[#245cea] hover:text-white rounded-t-md"
                  : idx === options.length - 1
                  ? "hover:bg-[#245cea] hover:text-white rounded-b-md"
                  : "hover:bg-[#245cea] hover:text-white"
              }`}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SortBy;
